import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Send, RefreshCw, RotateCcw, Search, AlertCircle, CheckCircle, Inbox, Loader2, MailWarning } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const emptyManualForm = { recipient: '', subject: '', body: '', type: 'manual' };

const typeColors = {
  manual: 'bg-slate-700 text-slate-200',
  invitation: 'bg-blue-500/10 text-blue-300',
  registration_confirmation: 'bg-emerald-500/10 text-emerald-300',
  rsvp_confirmation: 'bg-purple-500/10 text-purple-300',
  test: 'bg-cyan-500/10 text-cyan-300',
};

const statusColors = {
  sent: 'bg-emerald-500/10 text-emerald-300',
  failed: 'bg-red-500/10 text-red-300',
  logged: 'bg-amber-500/10 text-amber-300',
  pending: 'bg-blue-500/10 text-blue-300',
};

export default function AdminEmails() {
  const { toast } = useToast();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [smtpStatus, setSmtpStatus] = useState(null);
  const [manualForm, setManualForm] = useState(emptyManualForm);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState('');
  const [selectedLog, setSelectedLog] = useState(null);
  const [activeTab, setActiveTab] = useState('inbox');
  const [inboxMessages, setInboxMessages] = useState([]);
  const [inboxStatus, setInboxStatus] = useState(null);
  const [inboxLoading, setInboxLoading] = useState(true);
  const [inboxError, setInboxError] = useState('');
  const [selectedInboxMessage, setSelectedInboxMessage] = useState(null);
  const [inboxMessageLoading, setInboxMessageLoading] = useState(false);

  const load = () => {
    apiGet('/api/email-logs')
      .then(r => setLogs(r.data.email_logs || []))
      .catch(() => {})
      .finally(() => setLoading(false));
    apiGet('/api/email/status').then(r => setSmtpStatus(r.data)).catch(() => {});
  };

  const loadInbox = () => {
    setInboxLoading(true);
    setInboxError('');
    apiGet('/api/email/inbox')
      .then(response => {
        setInboxStatus({
          enabled: response.data.enabled,
          configured: response.data.configured,
        });
        setInboxMessages(response.data.messages || []);
      })
      .catch(error => {
        setInboxError(error.response?.data?.detail || 'Indbakken kunne ikke hentes.');
      })
      .finally(() => setInboxLoading(false));
  };

  useEffect(() => {
    load();
    loadInbox();
  }, []);

  const openInboxMessage = async (message) => {
    setSelectedInboxMessage(message);
    setInboxMessageLoading(true);
    try {
      const response = await apiGet(`/api/email/inbox/${encodeURIComponent(message.id)}`);
      setSelectedInboxMessage(response.data.message);
    } catch (error) {
      toast({
        title: 'Mailen kunne ikke åbnes',
        description: error.response?.data?.detail || 'Prøv igen.',
        variant: 'destructive',
      });
      setSelectedInboxMessage(null);
    } finally {
      setInboxMessageLoading(false);
    }
  };

  const smtpReady = smtpStatus?.enabled && smtpStatus?.configured;
  const sentLogs = logs.filter(log => log.status === 'sent');
  const failedOrPendingLogs = logs.filter(log => log.status === 'failed' || log.status === 'logged');

  const filteredLogs = useMemo(() => {
    if (!search) return sentLogs;
    const s = search.toLowerCase();
    return sentLogs.filter(log =>
      log.recipient?.toLowerCase().includes(s) ||
      log.subject?.toLowerCase().includes(s) ||
      log.type?.toLowerCase().includes(s) ||
      log.status?.toLowerCase().includes(s)
    );
  }, [sentLogs, search]);

  const handleManualSend = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await apiPost('/api/email/send', manualForm);
      const status = res.data.log?.status;
      if (status === 'failed') {
        toast({
          title: 'Mail fejlede',
          description: res.data.log?.error || 'Mailen kunne ikke sendes.',
          variant: 'destructive',
        });
      } else {
        const sent = status === 'sent';
        toast({
          title: sent ? 'Mail sendt' : 'Mail logget',
          description: sent ? 'Mailen blev sendt via SMTP.' : 'SMTP er ikke konfigureret, så mailen blev kun logget.',
        });
        setManualForm(emptyManualForm);
      }
      load();
    } catch (err) {
      toast({ title: 'Mail fejlede', description: err.response?.data?.detail || 'Mailen kunne ikke sendes.', variant: 'destructive' });
    } finally {
      setSending(false);
    }
  };

  const handleResend = async (logId) => {
    try {
      const res = await apiPost(`/api/email/resend/${logId}`, {});
      const status = res.data.log?.status;
      if (status === 'failed') {
        toast({
          title: 'Send igen fejlede',
          description: res.data.log?.error || 'Mailen kunne ikke sendes igen.',
          variant: 'destructive',
        });
      } else {
        const sent = status === 'sent';
        toast({
          title: sent ? 'Mail sendt igen' : 'Mail logget igen',
          description: sent ? 'Mailen blev sendt via SMTP.' : 'SMTP er ikke konfigureret, så mailen blev kun logget.',
        });
      }
      load();
    } catch (err) {
      toast({ title: 'Send igen fejlede', description: err.response?.data?.detail || 'Mailen kunne ikke sendes igen.', variant: 'destructive' });
    }
  };

  const applyTemplate = (key) => {
    const templates = {
      invitation: {
        subject: 'Invitation til Molin30',
        body: 'Hej,\n\nDu er inviteret til Molin30. Brug din invitationskode til at registrere dig i portalen.\n\nVi ses!',
      },
      registration_confirmation: {
        subject: 'Velkommen til Molin30',
        body: 'Hej,\n\nDin registrering i Molin30-portalen er bekræftet.\n\nVenlig hilsen\nMolin30',
      },
      rsvp_confirmation: {
        subject: 'RSVP bekræftelse - Molin30',
        body: 'Hej,\n\nVi har modtaget dit RSVP-svar.\n\nVenlig hilsen\nMolin30',
      },
    };
    const template = templates[key];
    if (!template) return;
    setManualForm(form => ({ ...form, type: key, subject: template.subject, body: template.body }));
  };

  return (
    <div className="animate-fade-in space-y-6" data-testid="admin-emails-page">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-slate-50" style={{ fontFamily: "'Outfit', sans-serif" }}>Mailcenter</h1>
          <p className="text-sm text-slate-400 mt-1">Adminværktøj til eventkommunikation. Gæster får ingen indbakke.</p>
        </div>
        <Button variant="outline" onClick={() => { load(); if (activeTab === 'inbox') loadInbox(); }} className="w-full sm:w-auto bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700" data-testid="refresh-emails-btn">
          <RefreshCw className="w-4 h-4 mr-2" /> Opdater
        </Button>
      </div>

      <div className="flex gap-1 overflow-x-auto border-b border-slate-800 pb-px [-webkit-overflow-scrolling:touch]" role="tablist" aria-label="Mailcenter">
        {[
          ['inbox', 'Indbakke'],
          ['sent', 'Sendte mails'],
          ['failed', 'Fejlede mails'],
          ['compose', 'Send mail'],
          ['smtp', 'SMTP-status'],
        ].map(([key, label]) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={activeTab === key}
            onClick={() => setActiveTab(key)}
            className={`min-h-11 whitespace-nowrap border-b-2 px-4 py-3 text-sm transition-colors ${
              activeTab === key
                ? 'border-blue-400 text-blue-300'
                : 'border-transparent text-slate-500 hover:text-slate-300'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {activeTab === 'inbox' && (
        <InboxPanel
          messages={inboxMessages}
          status={inboxStatus}
          loading={inboxLoading}
          error={inboxError}
          onRefresh={loadInbox}
          onSelect={openInboxMessage}
        />
      )}

      {activeTab === 'smtp' && (
      <section className={`rounded-xl border p-5 ${smtpReady ? 'bg-emerald-500/5 border-emerald-500/20' : 'bg-amber-500/5 border-amber-500/20'}`} data-testid="smtp-status-section">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="flex items-start gap-3 min-w-0">
            {smtpReady ? <CheckCircle className="w-5 h-5 text-emerald-400 mt-0.5 flex-shrink-0" /> : <AlertCircle className="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0" />}
            <div className="min-w-0">
              <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-slate-200">SMTP-status</h2>
              <p className={`text-sm mt-2 ${smtpReady ? 'text-emerald-300' : 'text-amber-300'}`}>
                {smtpReady ? 'SMTP er konfigureret og klar til at sende mails.' : 'SMTP er ikke konfigureret. Mails bliver kun logget og ikke sendt.'}
              </p>
            </div>
          </div>
          <dl className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-xs min-w-0">
            <div><dt className="text-slate-500">Host</dt><dd className="text-slate-300 truncate">{smtpStatus?.host || '-'}</dd></div>
            <div><dt className="text-slate-500">Port</dt><dd className="text-slate-300">{smtpStatus?.port || '-'}</dd></div>
            <div><dt className="text-slate-500">Afsender</dt><dd className="text-slate-300 truncate">{smtpStatus?.from || '-'}</dd></div>
            <div><dt className="text-slate-500">TLS/SSL</dt><dd className="text-slate-300">{smtpStatus?.use_ssl ? 'SSL' : smtpStatus?.use_tls ? 'TLS' : 'Fra'}</dd></div>
          </dl>
        </div>
      </section>
      )}

      {activeTab === 'compose' && (
      <section className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5" data-testid="manual-email-section">
          <div className="flex items-center gap-2 mb-4">
            <Send className="w-4 h-4 text-blue-400" />
            <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-blue-400">Send mail</h2>
          </div>
          <form onSubmit={handleManualSend} className="space-y-3" data-testid="send-email-form">
            <select value={manualForm.type} onChange={e => { setManualForm(form => ({ ...form, type: e.target.value })); applyTemplate(e.target.value); }}
              className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none text-sm">
              <option value="manual">Manuel mail</option>
              <option value="invitation">Invitation</option>
              <option value="registration_confirmation">Registreringsbekræftelse</option>
              <option value="rsvp_confirmation">RSVP-bekræftelse</option>
            </select>
            <input type="email" placeholder="Modtagerens private e-mail" value={manualForm.recipient} onChange={e => setManualForm(form => ({ ...form, recipient: e.target.value }))}
              className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none text-sm" required data-testid="email-recipient-input" />
            <input type="text" placeholder="Emne" value={manualForm.subject} onChange={e => setManualForm(form => ({ ...form, subject: e.target.value }))}
              className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none text-sm" required data-testid="email-subject-input" />
            <textarea rows={5} placeholder="Besked" value={manualForm.body} onChange={e => setManualForm(form => ({ ...form, body: e.target.value }))}
              className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md px-3 py-2 outline-none text-sm resize-none" required data-testid="email-body-input" />
            <Button type="submit" disabled={sending} className="w-full sm:w-auto bg-blue-600 hover:bg-blue-500 text-white disabled:opacity-50" data-testid="email-send-submit">
              {sending ? 'Sender...' : 'Send mail'}
            </Button>
          </form>
      </section>
      )}

      {activeTab === 'failed' && (
      <section className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-5" data-testid="failed-pending-emails-section">
        <div className="flex items-center gap-2 mb-4">
          <MailWarning className="w-4 h-4 text-amber-400" />
          <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-amber-400">Fejlede mails og loggede mails</h2>
        </div>
        {failedOrPendingLogs.length === 0 ? (
          <p className="text-sm text-slate-500">Ingen fejlede eller loggede mails.</p>
        ) : (
          <EmailLogList logs={failedOrPendingLogs.slice(0, 8)} typeColors={typeColors} statusColors={statusColors} onResend={handleResend} onSelect={setSelectedLog} />
        )}
      </section>
      )}

      {activeTab === 'sent' && (
      <section className="space-y-4" data-testid="email-history-section">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3">
          <div>
            <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-blue-400">Sendte mails og loghistorik</h2>
            <p className="text-xs text-slate-500 mt-1">{sentLogs.length} sendt, {failedOrPendingLogs.length} fejlet/logget, {logs.length} i alt</p>
          </div>
          <div className="relative w-full lg:max-w-sm">
            <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
            <input type="text" placeholder="Søg i mails..." value={search} onChange={e => setSearch(e.target.value)}
              className="w-full bg-slate-900/50 border border-slate-700 focus:border-blue-500 text-slate-100 rounded-md pl-9 pr-3 py-2 outline-none text-sm"
              data-testid="email-search" />
          </div>
        </div>

        {loading ? (
          <p className="text-slate-400">Indlæser...</p>
        ) : filteredLogs.length === 0 ? (
          <p className="text-center py-8 text-slate-500 text-sm">Ingen mails fundet.</p>
        ) : (
          <EmailLogList logs={filteredLogs} typeColors={typeColors} statusColors={statusColors} onResend={handleResend} onSelect={setSelectedLog} table />
        )}
      </section>
      )}

      <EmailPreviewDialog
        log={selectedLog}
        sender={smtpStatus?.from_name && smtpStatus?.from
          ? `${smtpStatus.from_name} <${smtpStatus.from}>`
          : smtpStatus?.from || '-'}
        statusColors={statusColors}
        onClose={() => setSelectedLog(null)}
      />
      <InboxPreviewDialog
        message={selectedInboxMessage}
        loading={inboxMessageLoading}
        onClose={() => setSelectedInboxMessage(null)}
      />
    </div>
  );
}

function InboxPanel({ messages, status, loading, error, onRefresh, onSelect }) {
  const unavailable = status && (!status.enabled || !status.configured);

  return (
    <section className="space-y-4" data-testid="email-inbox-section">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-sm font-bold uppercase tracking-[0.2em] text-blue-400">Indbakke</h2>
          <p className="text-xs text-slate-500 mt-1">{messages.length} mails</p>
        </div>
        <Button type="button" variant="outline" onClick={onRefresh} disabled={loading || unavailable} className="w-full sm:w-auto bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700">
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Opdater
        </Button>
      </div>

      {unavailable ? (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-200">
          IMAP er ikke konfigureret. Indbakken er ikke tilgængelig.
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200">
          {error}
        </div>
      ) : loading ? (
        <div className="flex items-center gap-2 py-8 text-sm text-slate-400">
          <Loader2 className="w-4 h-4 animate-spin" /> Henter indbakke...
        </div>
      ) : messages.length === 0 ? (
        <div className="rounded-xl border border-slate-700/50 bg-slate-800/30 py-12 text-center">
          <Inbox className="w-8 h-8 mx-auto mb-3 text-slate-600" />
          <p className="text-sm text-slate-500">Indbakken er tom.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-700/50 bg-slate-800/30">
          {messages.map(message => (
            <button
              key={message.id}
              type="button"
              onClick={() => onSelect(message)}
              className="flex w-full min-w-0 items-start gap-3 border-b border-slate-700/30 p-4 text-left transition-colors last:border-0 hover:bg-slate-800/70"
              data-testid={`inbox-message-${message.id}`}
            >
              <span className={`mt-1.5 h-2 w-2 flex-shrink-0 rounded-full ${message.read ? 'bg-slate-700' : 'bg-blue-400'}`} aria-label={message.read ? 'Læst' : 'Ulæst'} />
              <div className="min-w-0 flex-1">
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                  <p className={`truncate text-sm ${message.read ? 'text-slate-400' : 'font-semibold text-slate-100'}`}>{message.from || 'Ukendt afsender'}</p>
                  <span className="flex-shrink-0 text-xs text-slate-600">{formatInboxDate(message.date)}</span>
                </div>
                <p className="mt-1 truncate text-sm text-slate-300">{message.subject || '(Intet emne)'}</p>
                <p className="mt-1 line-clamp-2 text-xs leading-relaxed text-slate-500">{message.snippet || 'Ingen tekstvisning.'}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </section>
  );
}

function InboxPreviewDialog({ message, loading, onClose }) {
  return (
    <Dialog open={!!message} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-slate-100 pr-8">Indgående mail</DialogTitle>
        </DialogHeader>
        {message && (
          <div className="space-y-5">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <PreviewField label="Emne" value={message.subject || '(Intet emne)'} />
              <PreviewField label="Status" value={message.read ? 'Læst' : 'Ulæst'} />
              <PreviewField label="Afsender" value={message.from || 'Ukendt afsender'} />
              <PreviewField label="Dato og tid" value={formatInboxDate(message.date)} />
            </dl>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Mailtekst</p>
              <div className="min-h-32 rounded-lg border border-slate-700/60 bg-slate-950/60 p-4 text-sm leading-relaxed text-slate-200 whitespace-pre-wrap break-words">
                {loading ? (
                  <span className="inline-flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Henter mail...</span>
                ) : (
                  message.body || 'Ingen mailtekst.'
                )}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function formatInboxDate(value) {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString('da-DK');
}

function EmailLogList({ logs, typeColors, statusColors, onResend, onSelect, table = false }) {
  if (table) {
    return (
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="email-logs-table">
            <thead>
              <tr className="border-b border-slate-700/50">
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Modtager</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Type</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Emne</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Tidspunkt</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Fejl</th>
                <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Handling</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => <EmailLogRow key={log.id} log={log} typeColors={typeColors} statusColors={statusColors} onResend={onResend} onSelect={onSelect} />)}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {logs.map(log => (
        <div
          key={log.id}
          role="button"
          tabIndex={0}
          onClick={() => onSelect(log)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') onSelect(log);
          }}
          className="rounded-xl border border-slate-700/50 bg-slate-900/40 p-4 cursor-pointer hover:border-blue-500/40 hover:bg-slate-900/70 focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="text-sm text-slate-200 truncate">{log.subject}</p>
              <p className="text-xs text-slate-500 truncate">{log.recipient}</p>
            </div>
            <StatusBadge log={log} statusColors={statusColors} />
          </div>
          {log.error && <p className="text-xs text-red-300 mt-2 break-words">{log.error}</p>}
          <div className="flex items-center justify-between gap-3 mt-3">
            <span className={`text-xs px-2 py-1 rounded ${typeColors[log.type] || 'bg-slate-700 text-slate-300'}`}>{log.type || 'manual'}</span>
            <button onClick={(event) => { event.stopPropagation(); onResend(log.id); }} className="inline-flex items-center gap-1 text-xs text-blue-400 hover:text-blue-300" data-testid={`resend-${log.id}`}>
              <RotateCcw className="w-3.5 h-3.5" /> Send igen
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function EmailLogRow({ log, typeColors, statusColors, onResend, onSelect }) {
  return (
    <tr
      role="button"
      tabIndex={0}
      onClick={() => onSelect(log)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') onSelect(log);
      }}
      className="border-b border-slate-700/20 hover:bg-slate-800/30 cursor-pointer focus:outline-none focus:bg-slate-800/50"
    >
      <td className="px-4 py-3 text-slate-300 text-xs">{log.recipient}</td>
      <td className="px-4 py-3"><span className={`text-xs px-2 py-1 rounded ${typeColors[log.type] || 'bg-slate-700 text-slate-300'}`}>{log.type || 'manual'}</span></td>
      <td className="px-4 py-3 text-slate-200 text-xs max-w-xs truncate">{log.subject}</td>
      <td className="px-4 py-3"><StatusBadge log={log} statusColors={statusColors} /></td>
      <td className="px-4 py-3 text-xs text-slate-400">{log.sent_at ? new Date(log.sent_at).toLocaleString('da-DK') : '-'}</td>
      <td className="px-4 py-3 text-xs text-red-300 max-w-xs truncate" title={log.error || ''}>{log.error || '-'}</td>
      <td className="px-4 py-3 text-right">
        <button onClick={(event) => { event.stopPropagation(); onResend(log.id); }} className="inline-flex items-center gap-1 px-2 py-1 rounded hover:bg-blue-500/10 text-slate-400 hover:text-blue-400" title="Send igen" data-testid={`resend-${log.id}`}>
          <RotateCcw className="w-3.5 h-3.5" /> <span className="hidden xl:inline">Send igen</span>
        </button>
      </td>
    </tr>
  );
}

function EmailPreviewDialog({ log, sender, statusColors, onClose }) {
  return (
    <Dialog open={!!log} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="bg-slate-900 border-slate-700 text-slate-100 sm:max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-slate-100 pr-8">Mailvisning</DialogTitle>
        </DialogHeader>
        {log && (
          <div className="space-y-5">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <PreviewField label="Emne" value={log.subject || '-'} />
              <PreviewField label="Status" value={<StatusBadge log={log} statusColors={statusColors} />} />
              <PreviewField label="Modtager" value={log.recipient || '-'} />
              <PreviewField label="Afsender" value={log.sender || sender} />
              <PreviewField label="Dato og tid" value={log.sent_at ? new Date(log.sent_at).toLocaleString('da-DK') : '-'} />
              <PreviewField label="Type" value={log.type || 'manuel'} />
            </dl>
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Mailtekst</p>
              <div className="rounded-lg border border-slate-700/60 bg-slate-950/60 p-4 text-sm leading-relaxed text-slate-200 whitespace-pre-wrap break-words">
                {log.body || 'Ingen mailtekst gemt.'}
              </div>
            </div>
            {log.error && (
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.15em] text-red-400 mb-2">Fejl</p>
                <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-200 break-words">
                  {log.error}
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function PreviewField({ label, value }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</dt>
      <dd className="mt-1 text-slate-200 break-words">{value}</dd>
    </div>
  );
}

function StatusBadge({ log, statusColors }) {
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded ${statusColors[log.status] || 'bg-slate-700 text-slate-300'}`} title={log.error || ''}>
      {log.status === 'sent' && <CheckCircle className="w-3 h-3" />}
      {log.status === 'failed' && <AlertCircle className="w-3 h-3" />}
      {log.status === 'sent' ? 'sendt' : log.status === 'failed' ? 'fejlet' : 'logget'}
    </span>
  );
}
